/**
 * DevModeContext
 * 
 * Global context for toggling dev/admin features.
 * Toggle with Ctrl+Shift+D or by clicking logo 5 times.
 * Persisted to localStorage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DevModeContext = createContext();

export function DevModeProvider({ children }) {
    const [isDevMode, setIsDevMode] = useState(() => {
        const saved = localStorage.getItem('kinkdb-devmode');
        return saved === 'true';
    });

    // Persist to localStorage
    useEffect(() => {
        localStorage.setItem('kinkdb-devmode', isDevMode.toString());
    }, [isDevMode]);

    // Keyboard shortcut: Ctrl+Shift+D
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                setIsDevMode(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const toggleDevMode = useCallback(() => {
        setIsDevMode(prev => !prev);
    }, []);

    return (
        <DevModeContext.Provider value={{ isDevMode, toggleDevMode }}>
            {children}
        </DevModeContext.Provider>
    );
}

export function useDevMode() {
    const context = useContext(DevModeContext);
    if (!context) {
        throw new Error('useDevMode must be used within a DevModeProvider');
    }
    return context;
}

export default DevModeContext;
